"use client";

import { useRouter } from "next/navigation";
import { FormEvent, useState } from "react";
import type { Dictionary, Locale } from "@/i18n/dictionaries";
import styles from "./authDialog.module.css";

export type AuthMode = "login" | "register";

type AuthResult = {
  ok: boolean;
  status?: string;
  message: string;
  user?: {
    account?: string;
    name?: string;
  };
};

type AuthDialogProps = {
  locale: Locale;
  labels: Dictionary["authPage"];
  mode: AuthMode;
  returnUrl: string;
  open?: boolean;
  embedded?: boolean;
  onClose?: () => void;
};

function validate(mode: AuthMode, account: string, password: string, confirmPassword: string, labels: Dictionary["authPage"]) {
  if (account.length > 30 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(account)) {
    return labels.accountMustEmail;
  }
  if (!password) {
    return labels.passwordRequired;
  }
  if (mode === "register" && password.length < 6) {
    return labels.passwordTooShort;
  }
  if (mode === "register" && password !== confirmPassword) {
    return labels.passwordMismatch;
  }
  return "";
}

export function AuthDialog({ locale, labels, mode, returnUrl, open = true, embedded = false, onClose }: Readonly<AuthDialogProps>) {
  const router = useRouter();
  const [currentMode, setCurrentMode] = useState<AuthMode>(mode);
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [autoLogin, setAutoLogin] = useState(false);
  const [agree, setAgree] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requestingCode, setRequestingCode] = useState(false);

  if (!open) {
    return null;
  }

  const isLogin = currentMode === "login";
  const helperQuery = `lang=${locale}&returnUrl=${encodeURIComponent(returnUrl)}`;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const error = validate(currentMode, account.trim(), password, confirmPassword, labels);
    if (error) {
      setMessage(error);
      return;
    }
    if (!isLogin && !agree) {
      setMessage(labels.agreementRequired);
      return;
    }
    if (!isLogin && !verificationCode.trim()) {
      setMessage(labels.verificationCodeRequired);
      return;
    }

    setSubmitting(true);
    setMessage("");
    const result: AuthResult = await fetch("/api/auth", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        mode: currentMode,
        account,
        password,
        confirmPassword,
        verificationCode,
        autoLogin,
        locale
      })
    })
      .then((response) => response.json() as Promise<AuthResult>)
      .catch(() => ({ ok: false, message: labels.systemError }));

    setSubmitting(false);
    setMessage(result.message || (result.ok ? labels.success : labels.systemError));
    if (result.ok) {
      const user = result.user || { account: account.trim(), name: account.trim() };
      window.dispatchEvent(new CustomEvent("auth:changed", { detail: { user } }));
      window.dispatchEvent(new Event("cart:refresh"));
      onClose?.();
      router.refresh();
      router.push(returnUrl);
    }
  }

  async function requestVerificationCode() {
    const cleanAccount = account.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanAccount)) {
      setMessage(labels.accountMustEmail);
      return;
    }

    setRequestingCode(true);
    setMessage("");
    const result = await fetch("/api/auth/register/verification", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ account: cleanAccount, locale }),
    })
      .then((response) => response.json() as Promise<{ ok?: boolean; message?: string; devCode?: string }>)
      .catch((): { ok: boolean; message: string; devCode?: string } => ({ ok: false, message: labels.systemError }));
    setRequestingCode(false);
    if (result.devCode) setVerificationCode(result.devCode);
    setMessage(result.message || (result.ok ? labels.verificationCodeSent : labels.systemError));
  }

  const dialog = (
    <div className={styles.dialog} role="dialog" aria-modal={!embedded} aria-label={`${labels.loginTitle} / ${labels.registerTitle}`}>
      <div className={styles.titleBar}>
        <button className={isLogin ? styles.activeTab : styles.tab} type="button" onClick={() => setCurrentMode("login")}>
          {labels.loginTitle}
        </button>
        <span>|</span>
        <button className={!isLogin ? styles.activeTab : styles.tab} type="button" onClick={() => setCurrentMode("register")}>
          {labels.registerTitle}
        </button>
        {onClose ? (
          <button className={styles.close} type="button" aria-label={labels.close} onClick={onClose}>
            X
          </button>
        ) : null}
      </div>

      <div className={styles.body}>
        <div className={styles.copy}>
          {isLogin ? (
            <>
              <p>{labels.loginManageAccount}</p>
              <p>{labels.loginViewModifyAccount}</p>
              <p>
                {labels.loginCreatePrefix}
                <button type="button" onClick={() => setCurrentMode("register")}>
                  {labels.loginCreateNow}
                </button>
              </p>
            </>
          ) : (
            <>
              <p>{labels.registerCreateAccount}</p>
              <p>{labels.registerLine2}</p>
              <p>{labels.registerLine3}</p>
            </>
          )}
        </div>

        <form className={styles.form} onSubmit={submit}>
          <label>
            {labels.accountLabel}
            <input
              value={account}
              type="email"
              maxLength={30}
              autoComplete="username"
              placeholder={isLogin ? labels.emailPlaceholder : labels.registerEmailPlaceholder}
              onChange={(event) => setAccount(event.target.value)}
            />
          </label>
          <label>
            {labels.passwordLabel}
            <input
              value={password}
              type="password"
              autoComplete={isLogin ? "current-password" : "new-password"}
              placeholder={isLogin ? labels.passwordPlaceholder : labels.registerPasswordPlaceholder}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          {!isLogin ? (
            <>
              <label>
                {labels.confirmPasswordLabel}
                <input
                  value={confirmPassword}
                  type="password"
                  autoComplete="new-password"
                  placeholder={labels.confirmPasswordPlaceholder}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
              </label>
              <label>
                {labels.verificationCodeLabel}
                <span className={styles.codeRow}>
                  <input
                    value={verificationCode}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder={labels.verificationCodePlaceholder}
                    onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  />
                  <button type="button" onClick={requestVerificationCode} disabled={requestingCode}>
                    {requestingCode ? labels.verificationCodeSending : labels.verificationCodeSend}
                  </button>
                </span>
              </label>
            </>
          ) : null}

          {isLogin ? (
            <label className={styles.checkLine}>
              <input checked={autoLogin} type="checkbox" onChange={(event) => setAutoLogin(event.target.checked)} />
              {labels.autoLogin}
              <a href={`/auth/recover?${helperQuery}`}>{labels.forgotPassword}</a>
            </label>
          ) : (
            <label className={styles.checkLine}>
              <input checked={agree} type="checkbox" onChange={(event) => setAgree(event.target.checked)} />
              {labels.agreementPrefix}
              <a href={`/auth/agreement?${helperQuery}`}>
                {labels.agreementLink}
              </a>
            </label>
          )}

          {message ? <p className={styles.message}>{message}</p> : null}
          <button className={styles.submit} type="submit" disabled={submitting}>
            {submitting ? labels.submitting : isLogin ? labels.loginButton : labels.registerButton}
          </button>
        </form>
      </div>
    </div>
  );

  if (embedded) {
    return <div className={styles.embedded}>{dialog}</div>;
  }

  return <div className={styles.overlay}>{dialog}</div>;
}
